/**
 * Admin → Alertas.
 *
 * Tabs:
 *   - Regras       — quais condições disparam, por ativo ou wildcard
 *   - Canais       — onde mandar (email/webhook/telegram), com test button
 *   - Subscriptions— liga channel ↔ regras/ativos
 *   - Histórico    — alert_events com filtros
 *
 * super_admin precisa escolher um tenant antes (regras/canais são por-tenant).
 * Histórico funciona sem seletor — mostra agregado de todos.
 */

import { useState } from "react";
import { Building2 } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Tabs } from "@/components/Tabs";
import { Card, CardBody } from "@/components/Card";
import { Field, Select } from "@/components/Field";
import { EmptyState } from "@/components/EmptyState";
import { useActiveAlerts, useTenants } from "@/hooks/queries";

import { AlertRulesTab } from "./alerts/AlertRulesTab";
import { ChannelsTab } from "./alerts/ChannelsTab";
import { SubscriptionsTab } from "./alerts/SubscriptionsTab";
import { EventsTab } from "./alerts/EventsTab";

const TABS = ["rules", "channels", "subscriptions", "events"] as const;
type TabKey = (typeof TABS)[number];

export function AdminAlertsPage() {
  const auth = useAuth();
  const tenants = useTenants(auth.isSuperAdmin);

  const [active, setActive] = useState<TabKey>("rules");
  const [pickedTenantId, setPickedTenantId] = useState<string>("");

  // super_admin → tenant_id explícito; admin → undefined (backend usa o JWT)
  const tenantId = auth.isSuperAdmin
    ? pickedTenantId || undefined
    : undefined;

  const activeAlerts = useActiveAlerts(tenantId);

  // Tabs que exigem tenant escolhido (regras/canais/subscriptions são por-tenant)
  const requiresTenant = active !== "events";
  const showsPicker = auth.isSuperAdmin && requiresTenant;
  const blockedByPicker = showsPicker && !pickedTenantId;

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Gerenciar alertas"
        description="O que vigiar e quem avisar. Os alertas que dispararam ficam em Ocorrências."
      />

      <Tabs
        active={active}
        onChange={(k) => setActive(k as TabKey)}
        tabs={[
          { key: "rules",         label: "Regras" },
          { key: "channels",      label: "Canais" },
          { key: "subscriptions", label: "Subscriptions" },
          {
            key: "events",
            label: "Histórico",
            count: activeAlerts.data?.length ?? 0,
          },
        ]}
      />

      {/* Seletor de tenant pra super_admin nas tabs por-tenant */}
      {showsPicker && (
        <Card className="mt-6">
          <CardBody>
            <Field
              label={
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-brand-600" />
                  Tenant
                </span>
              }
            >
              <Select
                value={pickedTenantId}
                onChange={(e) => setPickedTenantId(e.target.value)}
                options={(tenants.data ?? []).map((t) => ({
                  value: t.id,
                  label: `${t.name} (${t.slug})`,
                }))}
                placeholder="Selecione um tenant..."
              />
            </Field>
          </CardBody>
        </Card>
      )}

      <div className="mt-6">
        {blockedByPicker ? (
          <EmptyState
            title="Selecione um tenant"
            description="Regras, canais e subscriptions são por cliente. Escolha qual deles você quer gerenciar acima."
          />
        ) : (
          <>
            {active === "rules"         && <AlertRulesTab tenantId={tenantId} />}
            {active === "channels"      && <ChannelsTab tenantId={tenantId} />}
            {active === "subscriptions" && <SubscriptionsTab tenantId={tenantId} />}
            {active === "events"        && <EventsTab tenantId={tenantId} />}
          </>
        )}
      </div>
    </div>
  );
}
